import {
  Box,
  IconButton,
  Stack,
  TextField,
  Button,
  Typography,
} from "@mui/material";
import FacebookIcon from "@mui/icons-material/Facebook";
import {
  Instagram,
  Language,
  LinkedIn,
  Twitter,
  WhatsApp,
  YouTube,
} from "@mui/icons-material";
import { useState } from "react";
import { tawkOpenChat } from "./tawk";
import { MuiTelInput } from "mui-tel-input";
import { useGetFooterConfigQuery } from "../../../service/footerConfigApi";

const FOOTER_SOURCE = "MMS";

const SOCIAL_ICON_MAP: Record<
  string,
  { Icon: React.ElementType; color: string }
> = {
  Facebook: { Icon: FacebookIcon, color: "#1877F2" },
  LinkedIn: { Icon: LinkedIn, color: "#0A66C2" },
  YouTube: { Icon: YouTube, color: "#FF0000" },
  WhatsApp: { Icon: WhatsApp, color: "#25D366" },
  Instagram: { Icon: Instagram, color: "#E4405F" },
  Twitter: { Icon: Twitter, color: "#1DA1F2" },
};

const fallbackContacts = [
  {
    location: "Zimbabwe Branch",
    phone: "+263 77 640 2402",
    email: "cas@area46.co.zw",
    address: "46 Mt Pleasant Drive,Harare",
  },
];

const fallbackSocialLinks = [
  {
    Icon: FacebookIcon,
    color: "#1877F2",
    href: "https://www.facebook.com/share/18wBS1THMb/",
  },
  {
    Icon: LinkedIn,
    color: "#0A66C2",
    href: "https://www.linkedin.com/company/muzukuru/",
  },
  {
    Icon: YouTube,
    color: "#FF0000",
    href: "https://www.youtube.com/@Muzukuru",
  },
  {
    Icon: WhatsApp,
    color: "#25D366",
    href: "https://wa.me/263776402402",
  },
];

const navLinks = [
  "Home",
  "Our Service",
  "Why Us",
  "More Products",
  "Contact Us",
];

const LandingFooter = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });

  const { data: footerConfig } = useGetFooterConfigQuery(FOOTER_SOURCE);

  const handleSend = () => {
    if (!form.name && !form.email && !form.phone) return;
    tawkOpenChat(form);
    setForm({ name: "", phone: "", email: "", message: "" });
  };

  const socialLinks =
    footerConfig?.socials && footerConfig.socials.length > 0
      ? footerConfig.socials.map((s) => {
          const match = SOCIAL_ICON_MAP[s.icon] ?? SOCIAL_ICON_MAP[s.platform];
          return {
            Icon: match?.Icon ?? Language,
            color: match?.color ?? "#666",
            href: s.url,
          };
        })
      : fallbackSocialLinks;

  const contacts =
    footerConfig?.contact_details && footerConfig.contact_details.length > 0
      ? [
          {
            location: "Zimbabwe Branch",
            phone:
              footerConfig.contact_details.find((c) => c.type === "phone")
                ?.value ?? "",
            email:
              footerConfig.contact_details.find((c) => c.type === "email")
                ?.value ?? "",
            address:
              footerConfig.contact_details.find((c) => c.type === "address")
                ?.value ?? "",
          },
        ]
      : fallbackContacts;

  return (
    <Box
      component="footer"
      id="contact"
      sx={{
        bgcolor: "#fff",
        px: { xs: 2, sm: 3, md: 6, lg: 6 },
        py: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      {/* Main Footer */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "1440px",
          bgcolor: "#825639",
          borderRadius: "40px",
          p: { xs: 3, md: 4 },
          position: "relative",
          zIndex: 20,
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "0.8fr 1.2fr",
            },
            gap: { xs: 5, lg: 10 },
          }}
        >
          {/* LEFT CONTACT CARD */}
          <Box
            sx={{
              bgcolor: "#B07650",
              borderRadius: "40px",
              p: 4,
              minHeight: 460,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: "rgba(255,255,255,.95)",
                  fontSize: 20,
                  mb: 5,
                  fontWeight: 500,
                }}
              >
                Our Contacts
              </Typography>

              <Stack spacing={5}>
                {contacts.map((contact, index) => (
                  <Box key={index}>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,.7)",
                        fontSize: 14,
                        mb: 1,
                      }}
                    >
                      {contact.location}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: 15,
                        lineHeight: 1.8,
                      }}
                    >
                      {contact.phone}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: 15,
                        lineHeight: 1.8,
                      }}
                    >
                      {contact.email}
                    </Typography>

                    <Typography
                      sx={{
                        color: "#fff",
                        fontSize: 15,
                        lineHeight: 1.8,
                      }}
                    >
                      {contact.address}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {/* Socials */}
            <Box mt={6}>
              <Typography
                sx={{
                  color: "rgba(255,255,255,.7)",
                  fontSize: 12,
                  textTransform: "uppercase",
                  letterSpacing: 1.5,
                  mb: 2,
                }}
              >
                Social Media
              </Typography>

              <Stack direction="row" spacing={1.5}>
                {socialLinks.map(({ Icon, color, href }, index) => (
                  <IconButton
                    key={index}
                    component="a"
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      width: 40,
                      height: 40,

                      bgcolor: "#fff",

                      color,

                      transition: "all .2s ease",

                      "&:hover": {
                        bgcolor: "#fff",
                        transform: "translateY(-2px) scale(1.05)",
                      },
                    }}
                  >
                    <Icon fontSize="small" />
                  </IconButton>
                ))}
              </Stack>
            </Box>
          </Box>

          {/* RIGHT SIDE */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              minHeight: 460,
              pt: 2,
            }}
          >
            {/* FORM */}
            <Stack spacing={4}>
              <Box
                sx={{ borderBottom: "1px solid rgba(255,255,255,.4)", pb: 1 }}
              >
                <Typography
                  sx={{ color: "rgba(255,255,255,.8)", fontSize: 12, mb: 1 }}
                >
                  Your Name
                </Typography>
                <TextField
                  fullWidth
                  variant="standard"
                  value={form.name}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, name: e.target.value }))
                  }
                  InputProps={{
                    disableUnderline: true,
                    sx: { color: "#fff", fontSize: 16 },
                  }}
                />
              </Box>
              <Box
                sx={{ borderBottom: "1px solid rgba(255,255,255,.4)", pb: 1 }}
              >
                <Typography
                  sx={{ color: "rgba(255,255,255,.8)", fontSize: 12, mb: 1 }}
                >
                  Your Phone Number
                </Typography>
                <MuiTelInput
                  fullWidth
                  variant="standard"
                  defaultCountry="ZW"
                  preferredCountries={[
                    "ZW",
                    "ZA",
                    "ZM",
                    "BW",
                    "MZ",
                    "MW",
                    "GB",
                    "US",
                  ]}
                  value={form.phone}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, phone: value }))
                  }
                  slotProps={{ input: { disableUnderline: true } }}
                  sx={{
                    "& .MuiInput-root": { color: "#fff", fontSize: 16 },
                    "& .MuiInput-root:before": {
                      borderBottomColor: "rgba(255,255,255,.4)",
                    },
                    "& .MuiInput-root:hover:not(.Mui-disabled):before": {
                      borderBottomColor: "rgba(255,255,255,.7)",
                    },
                    "& .MuiInput-root:after": { borderBottomColor: "#fff" },
                    "& .MuiTelInput-Flag": { mr: 1 },
                  }}
                />
              </Box>
              {[
                { label: "Your Email", field: "email", multiline: false },
                { label: "Your Message", field: "message", multiline: true },
              ].map(({ label, field, multiline }) => (
                <Box
                  key={field}
                  sx={{ borderBottom: "1px solid rgba(255,255,255,.4)", pb: 1 }}
                >
                  <Typography
                    sx={{ color: "rgba(255,255,255,.8)", fontSize: 12, mb: 1 }}
                  >
                    {label}
                  </Typography>
                  <TextField
                    fullWidth
                    variant="standard"
                    multiline={multiline}
                    rows={multiline ? 2 : 1}
                    value={form[field as keyof typeof form]}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, [field]: e.target.value }))
                    }
                    InputProps={{
                      disableUnderline: true,
                      sx: { color: "#fff", fontSize: 16 },
                    }}
                  />
                </Box>
              ))}

              <Box display="flex" justifyContent="flex-end">
                <Button
                  onClick={handleSend}
                  disabled={!form.name && !form.email && !form.phone}
                  sx={{
                    bgcolor: "#fff",
                    color: "#986A53",
                    px: 4,
                    py: 1.5,
                    borderRadius: "12px",
                    fontWeight: 600,
                    textTransform: "none",
                    "&:hover": { bgcolor: "rgba(255,255,255,.9)" },
                    "&.Mui-disabled": {
                      bgcolor: "rgba(255,255,255,.4)",
                      color: "#fff",
                    },
                  }}
                >
                  Chat With Us
                </Button>
              </Box>
            </Stack>

            {/* NAVIGATION */}
            <Box
              sx={{
                mt: 8,
                pt: 3,
                borderTop: "1px solid rgba(255,255,255,.1)",
                display: "flex",
                flexWrap: "wrap",
                gap: 3,
              }}
            >
              {navLinks.map((link) => (
                <Typography
                  key={link}
                  sx={{
                    color: "rgba(255,255,255,.9)",
                    fontSize: 14,
                    cursor: "pointer",

                    "&:hover": {
                      color: "#fff",
                    },
                  }}
                >
                  {link}
                </Typography>
              ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LandingFooter;
